import { isEmpty, keyBy } from 'lodash'
import { useMsg } from '~/common/localizationUtils'
import { Integer } from '~/common/types'
import { useErrorModal, useSuccessToast } from '~/design_system'
import { FixedPriceFormField } from '~/sow/details/fixed_price/FixedPriceForm'
import { MilestoneFormField } from '~/sow/details/milestone_payment/MilestonePaymentForm'
import { SOWWorkflowData, SOWWorkflowFormFields, SOWWorkflowSummary } from './types'

export const withPagination = (response: any, limit: Integer | undefined) => ({
  results: response.results,
  next_params: response.next_params,
  previous_params: response.previous_params,
  totalResults: response.count,
  totalPages: limit ? Math.ceil(response.count / limit) : 1,
  totalCount: response?.total_count ?? response?.count ?? 0,
  page: limit ? Math.ceil((response.offset + limit) / limit) : 1
})

export const useBasicResponseHandler = (successMessage = 'common.ui.success') => {
  // Works in conjunction with Standard Api Exceptions for APIs
  const successToast = useSuccessToast()
  const msg = useMsg()
  const { showModal: showErrorModal } = useErrorModal()
  return {
    onSuccess: () => successToast(msg(successMessage)),
    // @ts-ignore
    onError: async error => {
      await showErrorModal({
        error,
        title: error.body.title,
        message: error.body.message
      })
    }
  }
}

export function sowUrl(sowId: number, versionId: number): string {
  return `/api/v2/sow-module/sow/${sowId}/version/${versionId}`
}

export const makeWorkflowSummary = <T extends Record<string, string>>(
  workflows: SOWWorkflowData[] | undefined,
  workflowEnum: T
): SOWWorkflowSummary<T> => {
  const workflowsByName = keyBy(workflows, 'name')
  return (Object.values(workflowEnum) as string[]).reduce((acc, name) => {
    const workflow = workflowsByName[name]
    acc[name as T[keyof T]] = {
      name,
      limited_to_fields: workflow?.limited_to_fields ?? [],
      enabled: workflow?.enabled ?? false,
      unmet_prerequisites: workflow?.unmet_prerequisites ?? []
    }
    return acc
  }, {} as SOWWorkflowSummary<T>)
}

export const makeWorkflowEnabledFormFields = <T extends Record<string, string>>(
  workflow: SOWWorkflowData,
  formFieldsEnum: T
): SOWWorkflowFormFields<T> => {
  return (Object.values(formFieldsEnum) as string[]).reduce((acc, name) => {
    acc[name as T[keyof T]] = isEmpty(workflow.limited_to_fields) || workflow.limited_to_fields.includes(name)
    return acc
  }, {} as SOWWorkflowFormFields<T>)
}

export const isFormFieldEnabled = (
  fieldName: MilestoneFormField | FixedPriceFormField,
  workflowConfig?: SOWWorkflowData
): boolean => {
  if (!workflowConfig) {
    return false
  }

  return isEmpty(workflowConfig.limited_to_fields) || workflowConfig.limited_to_fields.includes(fieldName)
}

export const findUnmetPrerequisite = (workflowConfig: SOWWorkflowData, field: string) => {
  return workflowConfig.unmet_prerequisites.find(prerequisite => prerequisite.fields.includes(field))
}
